// First-Touch Attribution Tracking (DSGVO-konform: nur sessionStorage, kein Backend ohne Consent)
//
// Liest gclid + utm_* aus der URL beim ersten Aufruf einer Session und
// hält die Werte in sessionStorage, damit trackEvent sie nach Consent anhängen kann.

const ATTRIBUTION_KEY = "ff_attr";

export interface Attribution {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  gclid: string | null;
}

const empty = (): Attribution => ({
  utm_source: null,
  utm_medium: null,
  utm_campaign: null,
  gclid: null,
});

const hasAny = (a: Attribution): boolean =>
  Boolean(a.utm_source || a.utm_medium || a.utm_campaign || a.gclid);

/**
 * Liest gclid/utm_* aus der aktuellen URL und speichert sie als First-Touch
 * in sessionStorage. Schreibt nur, wenn noch keine Attribution vorhanden ist.
 * Idempotent — kann sicher mehrfach aufgerufen werden.
 */
export const captureAttributionFromUrl = (): void => {
  if (typeof window === "undefined") return;
  try {
    // Bereits gesetzt? Dann nicht überschreiben (First-Touch).
    if (sessionStorage.getItem(ATTRIBUTION_KEY)) return;

    const params = new URLSearchParams(window.location.search);
    const captured: Attribution = {
      utm_source: params.get("utm_source"),
      utm_medium: params.get("utm_medium"),
      utm_campaign: params.get("utm_campaign"),
      gclid: params.get("gclid"),
    };

    if (!hasAny(captured)) return;

    sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(captured));
  } catch {
    // sessionStorage kann blockiert sein (Privacy-Modus) — still ignore
  }
};

/**
 * Gibt die gespeicherte First-Touch-Attribution zurück oder ein leeres Objekt.
 * Wird von trackEvent / trackExit verwendet, um die Felder an den Insert zu hängen.
 */
export const getAttribution = (): Attribution => {
  if (typeof window === "undefined") return empty();
  try {
    const raw = sessionStorage.getItem(ATTRIBUTION_KEY);
    if (!raw) return empty();
    const parsed = JSON.parse(raw) as Partial<Attribution>;
    return {
      utm_source: parsed.utm_source ?? null,
      utm_medium: parsed.utm_medium ?? null,
      utm_campaign: parsed.utm_campaign ?? null,
      gclid: parsed.gclid ?? null,
    };
  } catch {
    return empty();
  }
};
